/**
 * Edit-Format — die strukturierte Änderungs-Payload, die das Edit-Tool (Server)
 * erzeugt und die der Client AI-unabhängig anwendet (PRD-0003-Engine).
 * Referenziert das Dokument NUR über `paragraph_id` (nie Office.js-Objekte);
 * der Server berührt keine docx-Bytes.
 *
 * Geteilter Vertrag (LO-48); baut auf PRD-0003 §7 auf und erweitert um `format`.
 */
import type { ParagraphId, ChangeId, TextAlignment } from "./document-status";
import type { Run, DocAlignment } from "./document-model";

/**
 * Schreib-Operation für Listen in EditContent — getrennt vom Lese-Typ DocListInfo.
 * none     → Absatz aus Liste lösen (detach).
 * continue → an die Liste des Referenzabsatzes anhängen (gleiche numId → Nummerierung zählt korrekt weiter).
 * level    → Ebene innerhalb der aktuellen Liste ändern.
 * new      → neue Liste anlegen (bullet oder nummeriert), kein existierender Anker nötig.
 *            Nur verwenden, wenn wirklich eine neue Liste beginnt — bei passendem bestehenden
 *            Listenstil stattdessen continue wählen.
 */
export type NumStyle = "bullet" | "arabic" | "upperRoman" | "lowerRoman" | "upperLetter" | "lowerLetter";

export type ListEdit =
  | { op: "none" }
  | { op: "continue"; ref_paragraph_id: string; level?: number }
  | { op: "level"; level: number }
  | { op: "new"; numStyle: NumStyle; level?: number };

export type ChangeOperation = "replace" | "insert" | "delete" | "format" | "cross_ref" | "insert_block";

/**
 * Strukturierter Zielinhalt eines Absatzes (replace/insert) — die KI beschreibt den
 * neuen Absatz in unserem Modell (Inline-`runs` + Absatz-Attribute), nicht als Plain-Text.
 */
export interface EditContent {
  runs: Run[]; // Inline-Läufe in Reihenfolge (Text + Marks)
  style?: string;
  align?: DocAlignment;
  list?: ListEdit;
  level?: number; // heading 1-6 (sonst Absatz)
  indent?: { left?: number; right?: number; firstLine?: number };
  spacing?: { before?: number; after?: number; line?: number };
  /**
   * Marker-/Aufzählungszeichen-FARBE setzen. Nur `color` wird angewandt (`"auto"` setzt zurück,
   * z. B. um ein rotes Bullet zu entfernen). `font`/`size` eines Markers stammen von der
   * Listenebene und werden beim Apply ignoriert — sie würden sonst den sichtbaren Text zerstören.
   */
  marker?: Run;
}

/**
 * Wie eine angenommene Änderung ins Word-Dokument geschrieben wird (Nutzer-Auswahl):
 * - `raw`: direkt, ohne Tracking, ohne Kommentar
 * - `with_comment`: ohne Tracking, KI-Begründung als Word-Kommentar
 * - `with_tracked_changes`: als Tracked Changes (Änderungsverfolgung an)
 * - `with_tracked_changes_and_comment`: Tracked Changes + Word-Kommentar
 */
export type ApplyMode = "raw" | "with_comment" | "with_tracked_changes" | "with_tracked_changes_and_comment";

export const applyModeTracks = (mode: ApplyMode): boolean =>
  mode === "with_tracked_changes" || mode === "with_tracked_changes_and_comment";
export const applyModeComments = (mode: ApplyMode): boolean =>
  mode === "with_comment" || mode === "with_tracked_changes_and_comment";

export interface ChangeFormat {
  styleName?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  size?: number;
  color?: string;
  highlight?: string;
  alignment?: TextAlignment;
}

/**
 * Neuer Querverweis = Bookmark am Ziel-Absatz + REF-Feld am Quell-Absatz.
 * Fehlende bookmark_name → generierter Name "_Ref<uid-Präfix>".
 * display_text → der Text im Quell-Absatz, der durch das REF-Feld ersetzt wird;
 *                fehlt er → Feld wird am Ende des Quell-Absatzes eingefügt.
 */
/** Ein einzelnes Verweisziel (Bookmark + Anzeigeart). */
export interface CrossRefTarget {
  target_paragraph_id: string;
  bookmark_name?: string;
  /**
   * Was das Feld anzeigt (Default `"number"`):
   * - `"number"`: die NUMMER des Ziels (z. B. "5.1"; Switch `\w \h`), zieht bei Umnummerierung nach.
   * - `"text"`: der TEXT des Ziels (Switch `\h`).
   */
  ref_kind?: "number" | "text";
}

export interface CrossRefEdit extends Partial<CrossRefTarget> {
  display_text?: string;
  /**
   * Zusammengesetzter Verweis: mehrere REF-Felder hintereinander. Nötig, wenn Nummer und Unterpunkt
   * aus VERSCHIEDENEN Nummernströmen stammen (eigene numId/Bookmark) — z. B. "2.1(a)" =
   * `[REF auf 2.1][REF auf (a)]`. Jedes Feld aktualisiert sich unabhängig. Ist `targets` gesetzt,
   * hat es Vorrang vor dem Einzel-`target_paragraph_id`.
   */
  targets?: CrossRefTarget[];
  /** Trenner zwischen den Feldern eines zusammengesetzten Verweises (Default `""` → "2.1(a)"). */
  separator?: string;
}

export interface ChangeProposal {
  id: ChangeId; // stabil — UI-Selektion/Tracking
  operation: ChangeOperation;
  paragraph_id: ParagraphId; // Anker; bei insert: Bezugspunkt; bei cross_ref: Quell-Absatz
  content?: EditContent; // replace/insert: strukturierter Zielinhalt (Runs+Attrs) — bevorzugt
  cross_ref?: CrossRefEdit; // nur bei operation:"cross_ref"
  /**
   * Nur replace: ganzen Absatz in EINER Operation ersetzen (HTML, ein Tracked Change).
   * Default false → granulares Ersetzen: nur die tatsächlich geänderten Wörter werden als
   * Tracked Change markiert. Die KI setzt das Flag, wenn der ganze Absatz oder große Teile
   * davon neu geschrieben/umstrukturiert werden.
   */
  replace_full?: boolean;
  new_text?: string; // Fallback/abwärtskompatibel; sonst aus content.runs abgeleitet
  /**
   * Nur `operation: "insert_block"`: mehrere Absätze als GEORDNETER Block in einem Vorschlag einfügen.
   * Reihenfolge = Dokumentreihenfolge. Jeder Eintrag ist ein vollständiger Absatz (runs + Attribute,
   * eigener `style`/`level`/`list`). Nötig für strukturierte Abschnitte (Titel + Unterziffern), die mit
   * Einzel-`insert` nicht zuverlässig geordnet werden können (neue Absätze sind nicht referenzierbar).
   */
  blocks?: EditContent[];
  position?: "before" | "after"; // insert/insert_block: Position relativ zum Anker (Default: after)
  format?: ChangeFormat; // optional; bei operation:"format" alleinstehend
  old_text?: string; // Ist-Text (Server füllt aus DocumentStatus) → Vorher/Nachher
  comment?: string; // KI-Begründung; in Kommentar-Apply-Modi als Word-Kommentar
}

export interface EditPayload {
  changes: ChangeProposal[]; // geordnet (Dokumentreihenfolge)
}
